import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { DollarSign, TrendingUp, Calendar, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

interface CommissionEarning {
  id: string;
  deliveredAt: Date;
  memorialName: string;
  serviceType: string;
  orderTotal: number;
  commission: number;
}

const PortalEarnings = () => {
  const navigate = useNavigate();
  const { isOwner, hasPermission, isLoading: authLoading, company } = usePortalAuth();
  const [totalCommission, setTotalCommission] = useState(0);
  const [monthlyCommission, setMonthlyCommission] = useState(0);
  const [weeklyCommission, setWeeklyCommission] = useState(0);
  const [earnings, setEarnings] = useState<CommissionEarning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination calculations
  const totalPages = Math.ceil(earnings.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedEarnings = earnings.slice(startIndex, endIndex);

  const canViewEarnings = isOwner || hasPermission('view_earnings');

  // Permission check - redirect if no access
  useEffect(() => {
    if (!authLoading && !canViewEarnings) {
      toast.error('You do not have permission to view earnings');
      navigate('/portal/dashboard');
    }
  }, [authLoading, canViewEarnings, navigate]);

  useEffect(() => {
    if (!canViewEarnings || authLoading) return;

    const fetchCommissionEarnings = async () => {
      if (!company?.id) return;

      try {
        const now = new Date();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);

        // Fetch delivered orders with commission — these are orders fulfilled by
        // 3rd-party partners where the monument company earns a profit share.
        // ORDER-SPLIT (self-fulfillment) orders have commission_amount_cents = null/0,
        // so the gt filter naturally excludes them without needing partner_type.
        const { data: orders, error } = await supabase
          .from('orders')
          .select(`
            id,
            delivered_at,
            service_type,
            total_cents,
            commission_amount_cents,
            memorial_id,
            memorials (
              first_name,
              last_name
            )
          `)
          .eq('monument_company_id', company.id)
          .eq('status', 'delivered')
          .gt('commission_amount_cents', 0)
          .order('delivered_at', { ascending: false });

        if (error) {
          console.error('Error fetching commission earnings:', error);
          return;
        }

        if (orders) {
          let total = 0;
          let monthly = 0;
          let weekly = 0;
          const earningsList: CommissionEarning[] = [];

          orders.forEach((order) => {
            const commission = (order.commission_amount_cents || 0) / 100;
            const orderTotal = (order.total_cents || 0) / 100;
            const deliveredDate = order.delivered_at ? new Date(order.delivered_at) : new Date();

            total += commission;

            if (deliveredDate >= monthStart && deliveredDate <= monthEnd) {
              monthly += commission;
            }
            if (deliveredDate >= weekStart && deliveredDate <= weekEnd) {
              weekly += commission;
            }

            const memorial = order.memorials as { first_name: string; last_name: string | null } | null;
            earningsList.push({
              id: order.id,
              deliveredAt: deliveredDate,
              memorialName: memorial
                ? buildMemorialDisplayName(memorial)
                : 'Unknown',
              serviceType: order.service_type,
              orderTotal,
              commission,
            });
          });

          setTotalCommission(total);
          setMonthlyCommission(monthly);
          setWeeklyCommission(weekly);
          setEarnings(earningsList);
        }
      } catch (error) {
        console.error('Error fetching commission earnings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommissionEarnings();
  }, [company?.id, canViewEarnings, authLoading]);

  if (authLoading || isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!canViewEarnings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profit Share Earnings</h1>
        <p className="text-muted-foreground">
          Commission earned when third-party partners fulfill services for your memorials
        </p>
      </div>

      {/* Commission Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">All Time</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalCommission.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Total commission earned</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${monthlyCommission.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">{format(new Date(), 'MMMM yyyy')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${weeklyCommission.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Current week</p>
          </CardContent>
        </Card>
      </div>

      {/* Earnings History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Earnings History</CardTitle>
          <CardDescription>
            Commission from orders fulfilled by third-party partners
          </CardDescription>
        </CardHeader>
        <CardContent>
          {earnings.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No commission earnings yet. When services are fulfilled for your memorials by partner companies, your profit share will appear here.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Memorial</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Order Total</TableHead>
                      <TableHead className="text-right">Your Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedEarnings.map((earning) => (
                      <TableRow key={earning.id}>
                        <TableCell>{format(earning.deliveredAt, 'MMM d, yyyy')}</TableCell>
                        <TableCell>{earning.memorialName}</TableCell>
                        <TableCell className="capitalize">{earning.serviceType}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          ${earning.orderTotal.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-600 dark:text-emerald-400">
                          ${earning.commission.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, earnings.length)} of {earnings.length} earnings
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fulfillment Portal cross-link — only for dual-role companies */}
      {company?.linked_fulfillment_partner_id && (
        <Card className="border-l-4 border-l-[#787559] bg-[#FFFEF5]">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6">
            <div>
              <h3 className="text-base font-semibold text-[#212121]">
                Looking for your fulfillment earnings?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Earnings from headstone cleanings you fulfill directly are tracked in the Fulfillment Portal.
              </p>
            </div>
            <Button
              onClick={() => navigate('/partner/earnings')}
              className="bg-[#787559] hover:bg-[#787559]/90 text-white shrink-0"
            >
              View Fulfillment Earnings
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalEarnings;
